/**
 * API Service Index
 * Zentrale Export-Datei für alle API-Module
 *
 * Bietet sowohl granulare Imports als auch einen kombinierten apiService
 * für Abwärtskompatibilität mit bestehendem Code.
 */

// Domain-spezifische API-Module importieren (MÜSSEN ganz oben stehen)
import { projectsApi } from "./projects";
import { areasApi } from "./areas";
import { parcelsApi } from "./parcels";
import { ownersApi } from "./owners";
import { yieldForecastsApi } from "./yieldForecasts";
import { costsApi } from "./costs";
import { tariffsApi } from "./tariffs";
import { usersApi } from "./users";
import { calculationsApi } from "./calculations";
import { financingsApi } from "./financings";
import { generationUnitsApi } from "./generationUnits";
import { anlagentypenApi } from "./anlagentypen";
import { companiesApi } from "./companies";
import { deadlinesApi } from "./deadlines";

// Basis HTTP-Client exportieren
export { api, getAuthHeaders, getBaseUrl, ApiError } from "./client";

// Domain-spezifische API-Module exportieren
export {
  anlagentypenApi,
  projectsApi,
  areasApi,
  parcelsApi,
  ownersApi,
  yieldForecastsApi,
  costsApi,
  tariffsApi,
  usersApi,
  calculationsApi,
  financingsApi,
  generationUnitsApi,
  companiesApi,
  deadlinesApi,
};

/**
 * Kombinierter API-Service für Abwärtskompatibilität
 * Vereint alle Domain-APIs in einem Objekt
 *
 * Empfehlung: Für neuen Code die spezifischen APIs importieren:
 * import { projectsApi } from '../services/api';
 */
export const apiService = {
  // Projekte
  fetch: projectsApi.fetch,
  getProjects: projectsApi.getProjects,
  getProjectsOverview: projectsApi.getProjectsOverview,
  getProject: projectsApi.getProject,
  getGesamtstatus: projectsApi.getGesamtstatus,
  getProjectKpis: projectsApi.getProjectKpis,
  createProject: projectsApi.createProject,
  updateProject: projectsApi.updateProject,
  updateProjectPartial: projectsApi.updateProjectPartial,
  deleteProject: projectsApi.deleteProject,
  getPlanOptions: projectsApi.getPlanOptions,
  getStatusOptions: projectsApi.getStatusOptions,

  // Areas (Flächen)
  getProjectArea: areasApi.getProjectArea,
  updateProjectArea: areasApi.updateProjectArea,
  uploadShapefile: areasApi.uploadShapefile,
  getFlaechenakquiseStatusOptions: areasApi.getFlaechenakquiseStatusOptions,

  // Parcels (Flurstücke)
  getParcels: parcelsApi.getParcels,
  getParcel: parcelsApi.getParcel,
  updateParcel: parcelsApi.updateParcel,
  updateParcelStatus: parcelsApi.updateParcelStatus,
  updateParcelNotes: parcelsApi.updateParcelNotes,

  // Owners (Eigentümer)
  getOwners: ownersApi.getOwners,
  getParcelOwners: ownersApi.getParcelOwners,
  getOwnerParcels: ownersApi.getOwnerParcels,
  getOwnersForParcels: ownersApi.getOwnersForParcels,
  getParcelsForOwners: ownersApi.getParcelsForOwners,
  updateOwnerStatus: ownersApi.updateOwnerStatus,
  updateOwnerNotes: ownersApi.updateOwnerNotes,
  importAlkisOwners: ownersApi.importAlkisOwners,

  // Yield Forecasts (Ertragsprognosen)
  getProjectErtragsprognosen: yieldForecastsApi.getProjectErtragsprognosen,
  getActiveProjectErtragsprognose:
    yieldForecastsApi.getActiveProjectErtragsprognose,
  getYieldForecast: yieldForecastsApi.getYieldForecast,
  createProjectErtragsprognose: yieldForecastsApi.createProjectErtragsprognose,
  updateYieldForecast: yieldForecastsApi.updateYieldForecast,
  deleteYieldForecast: yieldForecastsApi.deleteYieldForecast,

  // DeductionItems (Abzugspositionen)
  createDeductionItem: yieldForecastsApi.createDeductionItem,
  updateDeductionItem: yieldForecastsApi.updateDeductionItem,
  deleteDeductionItem: yieldForecastsApi.deleteDeductionItem,

  // Costs (CAPEX/OPEX) - Item-basiertes Modell
  getProjectCosts: costsApi.getProjectCosts,
  // CAPEX
  getCapex: costsApi.getCapex,
  createCapexItem: costsApi.createCapexItem,
  updateCapexItem: costsApi.updateCapexItem,
  deleteCapexItem: costsApi.deleteCapexItem,
  // OPEX
  getOpex: costsApi.getOpex,
  updateOpex: costsApi.updateOpex,
  createOpexItem: costsApi.createOpexItem,
  updateOpexItem: costsApi.updateOpexItem,
  deleteOpexItem: costsApi.deleteOpexItem,
  // Jährliche Kosten
  getYearlyCosts: costsApi.getYearlyCosts,
  updateYearlyValue: costsApi.updateYearlyValue,
  // LeaseTier (Pachtstaffeln)
  getLeaseTiers: costsApi.getLeaseTiers,
  createLeaseTier: costsApi.createLeaseTier,
  updateLeaseTier: costsApi.updateLeaseTier,
  deleteLeaseTier: costsApi.deleteLeaseTier,

  setGenehmigungOverride: costsApi.setGenehmigungOverride,
  updateCapexSectionLabel: costsApi.updateCapexSectionLabel,
  updateOpexSectionLabel: costsApi.updateOpexSectionLabel,

  // Tariffs (EEG/Marktpreis/PPA)
  getEEGTariff: tariffsApi.getEEGTariff,
  createOrUpdateEEGTariff: tariffsApi.createOrUpdateEEGTariff,
  updateEEGTariff: tariffsApi.updateEEGTariff,
  getMarktpreisTariff: tariffsApi.getMarktpreisTariff,
  createOrUpdateMarktpreisTariff: tariffsApi.createOrUpdateMarktpreisTariff,
  updateMarktpreisTariff: tariffsApi.updateMarktpreisTariff,
  getPPATariffs: tariffsApi.getPPATariffs,
  createPPATariff: tariffsApi.createPPATariff,
  updatePPATariff: tariffsApi.updatePPATariff,
  deletePPATariff: tariffsApi.deletePPATariff,

  // Energy Revenues (Energieerlöse)
  getEnergyRevenues: tariffsApi.getEnergyRevenues,
  calculateEnergyRevenues: tariffsApi.calculateEnergyRevenues,

  // Users (Benutzer)
  getCurrentUser: usersApi.getCurrentUser,
  updateUser: usersApi.updateUser,

  // Calculations (GuV, Cashflow)
  getProfitAndLoss: calculationsApi.getProfitAndLoss,
  calculateProfitAndLoss: calculationsApi.calculateProfitAndLoss,
  getProfitAndLossSummary: calculationsApi.getProfitAndLossSummary,
  deleteProfitAndLoss: calculationsApi.deleteProfitAndLoss,
  calculateCashflow: calculationsApi.calculateCashflow,
  calculateAll: calculationsApi.calculateAll,

  // Financing (Finanzierung)
  getDarlehensTypOptions: financingsApi.getDarlehensTypOptions,
  getFinanzierungStatusOptions: financingsApi.getFinanzierungStatusOptions,
  getFinancing: financingsApi.getFinancing,
  updateFinancing: financingsApi.updateFinancing,
  deleteFinancing: financingsApi.deleteFinancing,
  getSuggestedLoanAmount: financingsApi.getSuggestedLoanAmount,

  // Loans (Darlehen)
  getLoans: financingsApi.getLoans,
  getLoan: financingsApi.getLoan,
  createLoan: financingsApi.createLoan,
  updateLoan: financingsApi.updateLoan,
  deleteLoan: financingsApi.deleteLoan,
  getAggregatedRepaymentPlan: financingsApi.getAggregatedRepaymentPlan,
  updateAuszahlung: financingsApi.updateAuszahlung,

  // Auszahlungsplan (Detail-View)
  getAuszahlungen: financingsApi.getAuszahlungen,
  updateAuszahlungen: financingsApi.updateAuszahlungen,

  // Generation Units (Anlagen)
  getGenerationUnitOptions: generationUnitsApi.getGenerationUnitOptions,
  getProjectGenerationUnits: generationUnitsApi.getProjectGenerationUnits,
  createGenerationUnits: generationUnitsApi.createGenerationUnits,
  updateGenerationUnit: generationUnitsApi.updateGenerationUnit,
  deleteGenerationUnit: generationUnitsApi.deleteGenerationUnit,

  // Company (Gesellschaft & Gesellschafter)
  getCompany: companiesApi.getCompany,
  updateCompany: companiesApi.updateCompany,
  createShareholder: companiesApi.createShareholder,
  updateShareholder: companiesApi.updateShareholder,
  deleteShareholder: companiesApi.deleteShareholder,
};
